/**
 * Hook bridging DosimetryJobService to React components.
 *
 * Thin wrapper that subscribes to service events and rebuilds a snapshot.
 * All job orchestration logic lives in DosimetryJobService.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { DosimetryJobService, type DosimetryJobStatus } from '../services/dosimetry-job-service';

import { useVisualization } from '@/context';

// ============================================================================
// Types
// ============================================================================

export type { DosimetryJobStatus } from '../services/dosimetry-job-service';

export interface DosimetryJobState {
    /** Job status per perfused volume ID */
    jobStatus: ReadonlyMap<string, DosimetryJobStatus>;
    /** Error message per perfused volume ID */
    jobErrors: ReadonlyMap<string, string>;
    /** Run dosimetry for a specific perfused volume */
    runDosimetry: (pvId: string, segmentIndices: number[], tumorIndices: number[]) => void;
    /** Whether any job is currently running */
    hasRunningJobs: boolean;
    /** Top-level error when the initial job history load fails */
    loadError: string | null;
}

// ============================================================================
// Hook
// ============================================================================

export function useDosimetryJobs(caseId: string | undefined): DosimetryJobState {
    const { getServiceManager } = useVisualization();
    const serviceRef = useRef<DosimetryJobService | null>(null);

    const [jobStatus, setJobStatus] = useState<ReadonlyMap<string, DosimetryJobStatus>>(new Map());
    const [jobErrors, setJobErrors] = useState<ReadonlyMap<string, string>>(new Map());
    const [loadError, setLoadError] = useState<string | null>(null);

    const buildSnapshot = useCallback(() => {
        const service = serviceRef.current;
        if (!service) return;
        setJobStatus(new Map(service.getJobStatus()));
        setJobErrors(new Map(service.getJobErrors()));
    }, []);

    // Subscribe to service events + load existing jobs
    useEffect(() => {
        const sm = getServiceManager();
        const service = sm.getService<DosimetryJobService>(DosimetryJobService.SERVICE_NAME);
        serviceRef.current = service;

        const unsubscribers = [
            service.subscribe(service.EVENTS.JOB_STATUS_CHANGED, buildSnapshot),
            service.subscribe(service.EVENTS.LOAD_ERROR, (payload: unknown) => {
                const { message } = payload as { message: string };
                setLoadError(message);
            }),
        ];

        // Subscribe to PV removals for cascade cleanup
        if (caseId) {
            service.subscribeToPvRemovals(caseId);
            service.loadExistingJobs(caseId);
        }

        return () => {
            service.unsubscribeFromPvRemovals();
            serviceRef.current = null;
            unsubscribers.forEach((unsub) => unsub());
        };
    }, [getServiceManager, caseId, buildSnapshot]);

    const runDosimetry = useCallback(
        (pvId: string, segmentIndices: number[], tumorIndices: number[]) => {
            if (!caseId) return;
            serviceRef.current?.runDosimetry(caseId, pvId, segmentIndices, tumorIndices);
        },
        [caseId]
    );

    const hasRunningJobs = Array.from(jobStatus.values()).some((s) => s === 'queued' || s === 'processing');

    return {
        jobStatus,
        jobErrors,
        runDosimetry,
        hasRunningJobs,
        loadError,
    };
}
